import React from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { Card, Badge, Button, Input, Select } from '@/components/ui';
import type {
  Company,
  UserGroupInfo,
  UserFilter,
  InviteStatus,
} from '@/types/userManagement.types';

// Auto-sync component to handle form changes
const FormikAutoSync: React.FC<{
  onFiltersChange: (filters: UserFilter) => void;
}> = ({ onFiltersChange }) => {
  const { values } = useFormikContext<{
    search: string;
    companyId: string;
    userGroupId: string;
    inviteStatus: string;
  }>();

  React.useEffect(() => {
    const newFilters: UserFilter = {
      search: values.search || undefined,
      companyId: values.companyId ? parseInt(values.companyId) : undefined,
      userGroupId: values.userGroupId ? parseInt(values.userGroupId) : undefined,
      inviteStatus: (values.inviteStatus as InviteStatus) || undefined,
    };
    onFiltersChange(newFilters);
  }, [values, onFiltersChange]);

  return null;
};

interface UserFiltersProps {
  filters: UserFilter;
  onFiltersChange: (filters: UserFilter) => void;
  companies: Company[];
  userGroups: UserGroupInfo[];
  onClearFilters: () => void;
  inDrawer?: boolean;
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  onFiltersChange,
  companies,
  userGroups,
  onClearFilters,
  inDrawer = false,
}) => {
  // Convert filters to Formik initial values
  const initialValues = {
    search: filters.search || '',
    companyId: filters.companyId?.toString() || '',
    userGroupId: filters.userGroupId?.toString() || '',
    inviteStatus: filters.inviteStatus || '',
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== undefined && value !== ''
  );

  // Prepare options for Select components
  const companyOptions = [
    { value: '', label: 'All companies' },
    ...companies.map((company) => ({
      value: company.id.toString(),
      label: company.name,
    })),
  ];

  const userGroupOptions = [
    { value: '', label: 'All groups' },
    ...userGroups.map((group) => ({
      value: group.id.toString(),
      label: group.name,
    })),
  ];

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'Accepted', label: 'Accepted' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Declined', label: 'Declined' },
    { value: 'Expired', label: 'Expired' },
  ];

  return (
    <Card
      title={inDrawer ? undefined : 'Filters'}
      className={
        inDrawer ? 'rounded-none border-0 bg-transparent shadow-none dark:bg-transparent' : 'mb-6'
      }
      noPadding={inDrawer}
      footer={
        !inDrawer && hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={onClearFilters}>
            Clear all filters
          </Button>
        ) : null
      }
    >
      <Formik initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
        {({ values, setFieldValue }) => {
          const handleClearFilter = (key: keyof typeof values) => {
            setFieldValue(key, '');
          };

          return (
            <Form>
              <FormikAutoSync onFiltersChange={onFiltersChange} />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {/* Search Filter */}
                <Input
                  name="search"
                  label="Search"
                  placeholder="Search by name or email..."
                  leftIcon={
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  }
                />

                {/* Company Filter */}
                <Select name="companyId" label="Company" options={companyOptions} />

                {/* User Group Filter */}
                <Select name="userGroupId" label="User Group" options={userGroupOptions} />

                {/* Invite Status Filter */}
                <Select name="inviteStatus" label="Status" options={statusOptions} />
              </div>

              {/* Active Filter Display */}
              {hasActiveFilters && !inDrawer && (
                <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-600">
                  <div className="flex flex-wrap gap-2">
                    {filters.search && (
                      <Badge variant="primary" size="sm">
                        Search: {filters.search}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('search')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                          aria-label="Remove search filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}

                    {filters.companyId && (
                      <Badge variant="success" size="sm">
                        Company: {companies.find((c) => c.id === filters.companyId)?.name}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('companyId')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          aria-label="Remove company filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}

                    {filters.userGroupId && (
                      <Badge variant="default" size="sm">
                        Group: {userGroups.find((g) => g.id === filters.userGroupId)?.name}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('userGroupId')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                          aria-label="Remove user group filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}

                    {filters.inviteStatus && (
                      <Badge variant="warning" size="sm">
                        Status: {filters.inviteStatus}
                        <button
                          type="button"
                          onClick={() => handleClearFilter('inviteStatus')}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
                          aria-label="Remove status filter"
                        >
                          ×
                        </button>
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
};
